/**
 * Icon picker for V3 CMS list items — TREATX-V3-ICON-CMS-01.
 * Searchable grid over the shared curated registry, with a live preview of the
 * icon exactly as the public page renders it.
 */
import React, { useMemo, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import {
  DEFAULT_ITEM_ICON, V3_ICON_CATEGORIES, iconMeta, resolveIcon, searchIcons,
} from '../../lib/v3Icons';

export const IconPicker = ({ value, onChange, testid = 'icon-picker' }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const Current = resolveIcon(value);
  const meta = iconMeta(value);

  const results = useMemo(() => {
    const found = searchIcons(query);
    return category ? found.filter((i) => i.category === category) : found;
  }, [query, category]);

  const choose = (name) => { onChange(name); setOpen(false); };

  return (
    <div data-testid={testid}>
      <Label className="text-xs">Icon</Label>
      <div className="mt-1 flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white"
              data-testid={`${testid}-preview`}>
          <Current className="h-5 w-5 text-teal-700" strokeWidth={2} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-medium text-slate-700"
             data-testid={`${testid}-current`}>
            {meta ? meta.label : (value ? `${value} (not in the catalogue — showing the default)` : 'Default icon')}
          </p>
          <p className="truncate text-[10px] text-slate-400">{value || DEFAULT_ITEM_ICON}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button type="button" size="sm" variant="outline" data-testid={`${testid}-open`}>
              Change
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl" data-testid={`${testid}-dialog`}>
            <DialogHeader>
              <DialogTitle className="text-base">Choose an icon</DialogTitle>
            </DialogHeader>
            <Input autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
                   placeholder="Search — try quotation, verified, message, travel, payment…"
                   className="h-9 text-sm" data-testid={`${testid}-search`} />
            <div className="flex flex-wrap gap-1.5">
              <button type="button"
                      className={`rounded-full px-2.5 py-1 text-[11px] ${category === '' ? 'bg-[#122033] text-white' : 'border border-slate-200 bg-white text-slate-600'}`}
                      onClick={() => setCategory('')}
                      data-testid={`${testid}-cat-all`}>All</button>
              {V3_ICON_CATEGORIES.map((c) => (
                <button key={c} type="button"
                        className={`rounded-full px-2.5 py-1 text-[11px] ${category === c ? 'bg-[#122033] text-white' : 'border border-slate-200 bg-white text-slate-600'}`}
                        onClick={() => setCategory(c)}
                        data-testid={`${testid}-cat-${c.toLowerCase()}`}>{c}</button>
              ))}
            </div>
            <div className="max-h-[320px] overflow-y-auto">
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                {results.map(({ name, label, Icon }) => (
                  <button key={name} type="button" onClick={() => choose(name)}
                          title={`${label} (${name})`}
                          className={`flex flex-col items-center gap-1 rounded-lg border p-2 text-center transition-colors hover:border-teal-500 hover:bg-teal-50 ${name === value ? 'border-teal-600 bg-teal-50' : 'border-slate-200 bg-white'}`}
                          data-testid={`${testid}-option-${name}`}>
                    <Icon className="h-5 w-5 text-teal-700" strokeWidth={2} />
                    <span className="w-full truncate text-[10px] text-slate-600">{label}</span>
                  </button>
                ))}
              </div>
              {results.length === 0 && (
                <p className="py-6 text-center text-xs text-slate-400"
                   data-testid={`${testid}-empty`}>
                  No icon matches “{query}”.
                </p>
              )}
            </div>
            <p className="text-[10px] text-slate-400">
              {results.length} of the curated catalogue shown. The icon is saved with the
              item and rendered on the V3 homepage at the same size and stroke as the others.
            </p>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default IconPicker;
